import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Visibility as VisibilityIcon,
  Shield as ShieldIcon,
  AdminPanelSettings as AdminIcon,
  Storefront as StorefrontIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  ViewModule as GridViewIcon,
  ViewList as TableRowsIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_CONFIG } from '../../auth/config/authConfig';
import { Role, ModuleGroup, OperatorContext } from './types';
import { getHierarchyInfo } from './scopesConfig';
import { RoleMatrixEditor } from './components/RoleMatrixEditor';
import { RoleSimulatorPreview } from './components/RoleSimulatorPreview';
import { notify } from '../../notifications';

export const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<ModuleGroup[]>([]);
  const [operator, setOperator] = useState<OperatorContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Режим отображения: таблица (по умолчанию для чистоты) или карточки
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  // Фильтры
  const [searchTerm, setSearchTerm] = useState('');
  const [interfaceFilter, setInterfaceFilter] = useState<'all' | 'admin' | 'dashboard'>('all');

  // Модальные окна
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [simulatingRole, setSimulatingRole] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Загрузка ролей и каталога прав
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [rolesRes, catalogRes] = await Promise.all([
        axios.get(API_CONFIG.ROLES_V2_URL),
        axios.get(`${API_CONFIG.ROLES_V2_URL}/catalog`)
      ]);

      if (rolesRes.data?.success) {
        setRoles(rolesRes.data.roles || []);
        if (rolesRes.data.operator) {
          setOperator(rolesRes.data.operator);
        }
      }

      if (catalogRes.data?.success) {
        setModules(catalogRes.data.grouped || []);
      }
    } catch (err: any) {
      console.error('Ошибка загрузки данных системы ролей:', err);
      setError(err.response?.data?.message || err.message || 'Не удалось загрузить систему ролей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Фильтрация ролей
  const filteredRoles = useMemo(() => {
    return roles.filter(r => {
      const matchSearch =
        r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.description && r.description.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchInterface =
        interfaceFilter === 'all' || r.targetInterface === interfaceFilter;

      return matchSearch && matchInterface;
    });
  }, [roles, searchTerm, interfaceFilter]);

  const adminRolesCount = useMemo(() => roles.filter(r => r.targetInterface === 'admin').length, [roles]);
  const dashboardRolesCount = useMemo(() => roles.filter(r => r.targetInterface === 'dashboard').length, [roles]);

  // Создание новой роли
  const handleOpenCreate = () => {
    setEditingRole(null);
    setIsEditorOpen(true);
  };

  // Редактирование роли
  const handleOpenEdit = (role: Role) => {
    setEditingRole(role);
    setIsEditorOpen(true);
  };

  // Сохранение роли (создание или обновление)
  const handleSaveRole = async (roleData: any) => {
    if (editingRole) {
      const res = await axios.put(`${API_CONFIG.ROLES_V2_URL}/${editingRole.id}`, roleData);
      notify.success(res.data?.message || 'Роль успешно обновлена');
      setEditingRole((prev: any) => prev ? { ...prev, ...roleData } : null);
    } else {
      const res = await axios.post(API_CONFIG.ROLES_V2_URL, roleData);
      notify.success(res.data?.message || 'Роль успешно создана');
      if (res.data?.roleId) {
        setEditingRole({
          id: res.data.roleId,
          slug: res.data.slug,
          name: roleData.name,
          description: roleData.description,
          level: res.data.level,
          targetInterface: roleData.targetInterface,
          startPage: roleData.startPage,
          badge: roleData.badge,
          permissions: roleData.permissions,
          isSystem: false,
          userCount: 0,
          canEdit: true,
          canDelete: true
        });
      }
    }
    await fetchData();
  };

  // Подтверждение удаления роли
  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      setIsDeleting(true);
      const res = await axios.delete(`${API_CONFIG.ROLES_V2_URL}/${roleToDelete.id}`);
      notify.success(res.data?.message || 'Роль успешно удалена');
      setRoleToDelete(null);
      await fetchData();
    } catch (err: any) {
      notify.error(err.response?.data?.message || 'Ошибка удаления роли');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Toolbar ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2.5
        }}
      >
        {operator && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
              {operator.username}
            </Typography>
            <Box sx={{ px: 0.75, py: 0.15, borderRadius: '4px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', fontFamily: 'monospace' }}>
                Ранг {operator.roleLevel}
              </Typography>
            </Box>
          </Box>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          disabled={operator ? (!operator.canManageRoles && !operator.isSuperAdmin) : false}
          disableElevation
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontSize: '0.85rem' }}
        >
          Создать роль
        </Button>
      </Box>


      {/* Ошибки загрузки */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2.5, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchData}>
              Повторить
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* 2. Панель поиска, фильтра интерфейсов и переключателя вида */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2.5
        }}
      >
        {/* Поиск */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: { xs: '100%', sm: 280 }, maxWidth: 420 }}>
          <TextField
            placeholder="Поиск по роли или коду..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.875rem'
              }
            }}
          />

          <Tooltip title="Обновить список">
            <IconButton
              onClick={fetchData}
              size="small"
              sx={{
                p: 0.9,
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                '&:hover': { backgroundColor: '#f1f5f9' }
              }}
            >
              <RefreshIcon sx={{ color: '#64748b', fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Правая часть: Фильтр интерфейса + Переключатель вида (Таблица / Карточки) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Tabs
            value={interfaceFilter}
            onChange={(_, val) => setInterfaceFilter(val)}
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: 2,
              p: 0.5,
              border: '1px solid #e2e8f0',
              minHeight: 'auto',
              '& .MuiTab-root': {
                minHeight: 'auto',
                py: 0.6,
                px: 1.75,
                borderRadius: 1.5,
                fontSize: '0.82rem',
                fontWeight: 600,
                textTransform: 'none'
              }
            }}
          >
            <Tab value="all" label={`Все (${roles.length})`} />
            <Tab value="admin" label={`Админ (${adminRolesCount})`} />
            <Tab value="dashboard" label={`Аптека (${dashboardRolesCount})`} />
          </Tabs>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => { if (val) setViewMode(val); }}
            size="small"
            sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              p: 0.25,
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '6px !important',
                px: 1.25,
                py: 0.5,
                color: '#64748b',
                '&.Mui-selected': {
                  backgroundColor: '#0f172a',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#1e293b' }
                }
              }
            }}
          >
            <ToggleButton value="table" title="Вид: Таблица">
              <TableRowsIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton value="grid" title="Вид: Карточки">
              <GridViewIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* 3. Отображение списка: Таблица либо Сетка */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
          <CircularProgress size={36} sx={{ color: '#0f172a' }} />
        </Box>
      ) : filteredRoles.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed #cbd5e1',
            backgroundColor: '#ffffff'
          }}
        >
          <ShieldIcon sx={{ fontSize: 44, color: '#94a3b8', mb: 1, opacity: 0.4 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569' }}>
            Роли не найдены
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
            Попробуйте скорректировать поисковый запрос или фильтр интерфейса
          </Typography>
        </Paper>
      ) : viewMode === 'table' ? (
        /* ТАБЛИЧНЫЙ ВИД: ЧИСТЫЙ, АККУРАТНЫЙ И БЕЗ ПЕРЕГРУЗА */
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            overflow: 'hidden'
          }}
        >
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.5, pl: 2.5 }}>
                  РОЛЬ
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.5 }}>
                  РАНГ ДОСТУПА
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.5 }}>
                  ИНТЕРФЕЙС
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.5, textAlign: 'center' }}>
                  ПОЛЬЗОВАТЕЛИ
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.5, textAlign: 'center' }}>
                  ПРАВА
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.5 }}>
                  ОПИСАНИЕ
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.5, pr: 2.5, textAlign: 'right' }}>
                  ДЕЙСТВИЯ
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRoles.map((role) => {
                const hierarchy = getHierarchyInfo(role.level);
                const isAdminInterface = role.targetInterface === 'admin';
                const permCount = Object.keys(role.permissions || {}).length;

                return (
                  <TableRow
                    key={role.id}
                    hover
                    sx={{
                      transition: 'background-color 0.15s ease',
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    {/* Название роли */}
                    <TableCell sx={{ py: 1.25, pl: 2.5, minWidth: 180 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={role.name}
                          size="small"
                          sx={{
                            backgroundColor: role.badge.bg,
                            color: role.badge.text,
                            border: `1px solid ${role.badge.border}`,
                            fontWeight: 700,
                            fontSize: '0.8rem'
                          }}
                        />
                        {role.isSystem && (
                          <Tooltip title="Системная роль (защищена от удаления)">
                            <LockIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.72rem', display: 'block', mt: 0.25 }}>
                        {role.slug}
                      </Typography>
                    </TableCell>

                    {/* Ранг доступа */}
                    <TableCell sx={{ py: 1.25, minWidth: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: hierarchy.color,
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                          Ранг {role.level}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem', display: 'block' }}>
                        {hierarchy.category}
                      </Typography>
                    </TableCell>

                    {/* Рабочий интерфейс */}
                    <TableCell sx={{ py: 1.25, minWidth: 140 }}>
                      <Chip
                        icon={isAdminInterface ? <AdminIcon sx={{ fontSize: '14px !important' }} /> : <StorefrontIcon sx={{ fontSize: '14px !important' }} />}
                        label={isAdminInterface ? 'Админ-панель' : 'Кабинет Аптеки'}
                        size="small"
                        sx={{
                          backgroundColor: isAdminInterface ? '#eff6ff' : '#ecfdf5',
                          color: isAdminInterface ? '#1d4ed8' : '#047857',
                          border: `1px solid ${isAdminInterface ? '#bfdbfe' : '#a7f3d0'}`,
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>

                    {/* Пользователи */}
                    <TableCell sx={{ py: 1.25, textAlign: 'center', minWidth: 100 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: role.userCount > 0 ? '#1e293b' : '#94a3b8' }}>
                        {role.userCount}
                      </Typography>
                    </TableCell>

                    {/* Активные права */}
                    <TableCell sx={{ py: 1.25, textAlign: 'center', minWidth: 90 }}>
                      <Chip
                        label={permCount}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          backgroundColor: permCount > 0 ? '#f0fdf4' : '#f8fafc',
                          color: permCount > 0 ? '#16a34a' : '#94a3b8',
                          border: `1px solid ${permCount > 0 ? '#bbf7d0' : '#e2e8f0'}`
                        }}
                      />
                    </TableCell>

                    {/* Описание */}
                    <TableCell sx={{ py: 1.25, maxWidth: 280 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.3
                        }}
                      >
                        {role.description || '-'}
                      </Typography>
                    </TableCell>

                    {/* Действия */}
                    <TableCell sx={{ py: 1.25, pr: 2.5, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenEdit(role)}
                          disabled={!role.canEdit}
                          sx={{
                            height: 28,
                            px: 1.25,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 1.5,
                            borderColor: '#cbd5e1',
                            color: '#1e293b',
                            '&:hover': {
                              borderColor: '#94a3b8',
                              backgroundColor: '#f8fafc'
                            }
                          }}
                        >
                          Настроить
                        </Button>

                        <Tooltip title="Симулятор доступа">
                          <IconButton
                            size="small"
                            onClick={() => setSimulatingRole(role)}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1.5,
                              color: '#64748b',
                              '&:hover': { backgroundColor: '#f1f5f9' }
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={
                            role.isSystem
                              ? 'Системная роль'
                              : role.userCount > 0
                                ? `Привязано ${role.userCount} пользователей`
                                : !role.canDelete
                                  ? 'Недостаточно прав'
                                  : 'Удалить роль'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setRoleToDelete(role)}
                              disabled={!role.canDelete}
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1.5,
                                color: '#ef4444',
                                '&:hover': { backgroundColor: '#fee2e2' },
                                '&.Mui-disabled': { color: '#cbd5e1' }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* СЕТОЧНЫЙ ВИД (КАРТОЧКИ): 3 КОЛОНКИ, БЕЗ ЛИШНЕГО ШУМА */
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 2 }}>
          {filteredRoles.map((role) => {
            const hierarchy = getHierarchyInfo(role.level);
            const isAdminInterface = role.targetInterface === 'admin';
            const permCount = Object.keys(role.permissions || {}).length;

            return (
              <Card
                key={role.id}
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Верхняя строка карточки */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={role.name}
                        size="small"
                        sx={{
                          backgroundColor: role.badge.bg,
                          color: role.badge.text,
                          border: `1px solid ${role.badge.border}`,
                          fontWeight: 700,
                          fontSize: '0.8rem'
                        }}
                      />
                      <Chip
                        label={`Ранг ${role.level}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          backgroundColor: hierarchy.color,
                          color: '#ffffff'
                        }}
                      />
                    </Box>

                    <Chip
                      icon={isAdminInterface ? <AdminIcon sx={{ fontSize: '13px !important' }} /> : <StorefrontIcon sx={{ fontSize: '13px !important' }} />}
                      label={isAdminInterface ? 'Админ' : 'Аптека'}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: isAdminInterface ? '#eff6ff' : '#ecfdf5',
                        color: isAdminInterface ? '#1d4ed8' : '#047857',
                        border: `1px solid ${isAdminInterface ? '#bfdbfe' : '#a7f3d0'}`
                      }}
                    />
                  </Box>

                  {/* Описание роли */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontSize: '0.78rem',
                      lineHeight: 1.35,
                      minHeight: 34,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 1.5
                    }}
                  >
                    {role.description || 'Описание не указано'}
                  </Typography>

                  {/* Компактная плашка статистики */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: '#f8fafc',
                      border: '1px solid #f1f5f9',
                      mb: 1.5,
                      mt: 'auto'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#475569' }}>
                      <PeopleIcon sx={{ fontSize: 15, color: '#3b82f6' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                        {role.userCount} польз.
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#475569' }}>
                      <ShieldIcon sx={{ fontSize: 15, color: '#10b981' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                        {permCount} прав
                      </Typography>
                    </Box>

                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {role.slug}
                    </Typography>
                  </Box>

                  {/* Кнопки действий */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #f1f5f9' }}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                      onClick={() => setSimulatingRole(role)}
                      sx={{
                        color: '#64748b',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        p: 0.5
                      }}
                    >
                      Симулятор
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleOpenEdit(role)}
                        disabled={!role.canEdit}
                        sx={{
                          height: 28,
                          px: 1,
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: 1.5,
                          borderColor: '#cbd5e1',
                          color: '#1e293b'
                        }}
                      >
                        Матрица
                      </Button>

                      {role.canDelete && !role.isSystem && (
                        <IconButton
                          size="small"
                          onClick={() => setRoleToDelete(role)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            color: '#ef4444',
                            '&:hover': { backgroundColor: '#fee2e2' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* 4. Модальный редактор матрицы прав */}
      {operator && (
        <RoleMatrixEditor
          open={isEditorOpen}
          role={editingRole}
          modules={modules}
          availableRoles={roles}
          operator={operator}
          onClose={() => setIsEditorOpen(false)}
          onSave={handleSaveRole}
        />
      )}

      {/* 5. Модальный симулятор аудита доступа */}
      <RoleSimulatorPreview
        open={Boolean(simulatingRole)}
        role={simulatingRole}
        onClose={() => setSimulatingRole(null)}
      />

      {/* 6. Диалог подтверждения удаления */}
      <Dialog
        open={Boolean(roleToDelete)}
        onClose={() => setRoleToDelete(null)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#991b1b' }}>
          Удаление роли: {roleToDelete?.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#475569' }}>
            Вы действительно хотите безвозвратно удалить роль <strong>{roleToDelete?.name}</strong>?
            Все назначенные разрешения для этой роли будут аннулированы.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRoleToDelete(null)} disabled={isDeleting} sx={{ color: '#64748b' }}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            sx={{ fontWeight: 700 }}
          >
            {isDeleting ? <CircularProgress size={20} color="inherit" /> : 'Удалить роль'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
